<template>
    <div
        :class="[
            {
                'has-error': v && v.$anyError,
                'is-required': isRequired,
            },
            classes,
            name ? name : '',
        ]"
        class="field"
        :disabled="iniDisabled">
        <label class="label" v-if="label">{{ label }}</label>

        <!--TEXT-->
        <div
            :class="{
                'field has-addons': !!this.$slots.append || !!this.$slots.prepend,
            }">
            <!--prepend-->
            <div class="control" v-if="!!this.$slots.prepend">
                <slot name="prepend" />
            </div>

            <div class="control is-expanded">
                <template v-if="iniDisabled">
                    <!--DISABLED VIEW-->
                    <div class="control" v-if="dateFromNow && type != 'HOURS'">
                        <div class="input disabled">
                            <dateFromNow v-if="selected" :iniDate="selected" :iniDateFormat="dateFormat" />
                        </div>
                    </div>
                    <span v-else class="input disabled">{{ readOnlyValue }}</span>
                </template>
                <template v-else>
                    <!--EDIT VIEW-->
                    <!--DATE-->
                    <template v-if="type == 'DATE'">
                        <b-datepicker
                            v-model="selectedAsDate"
                            :placeholder="placeholder ? placeholder : $t('clickToSelect')"
                            :disabled="iniDisabled"
                            :date-formatter="formatDate"
                            icon="calendar-alt"
                            :append-to-body="appendToBody"
                            :position="position"
                            @input="onChange"
                            :min-date="minDate"
                            :editable="isEditable"
                            :first-day-of-week="1"
                            :focusedDate="focusedDate"></b-datepicker>
                    </template>
                    <!--DATETIME-->
                    <template v-else-if="type == 'DATETIME'">
                        <b-datetimepicker
                            v-model="selectedAsDate"
                            :placeholder="placeholder ? placeholder : $t('clickToSelect')"
                            :disabled="iniDisabled"
                            :datetime-formatter="formatDateTime"
                            icon="calendar-day"
                            :append-to-body="appendToBody"
                            :position="position"
                            @input="onChange"
                            :min-date="minDate"
                            :editable="isEditable"
                            :focusedDate="focusedDate"
                            :timepicker="timepickerOptions"
                            :datepicker="datepickerOptions">
                        </b-datetimepicker>
                    </template>
                    <!--TIME-->
                    <template v-else-if="type == 'TIME'">
                        <b-field>
                            <b-input :disabled="iniDisabled" v-model="hours" />
                            <p class="control">
                                <span class="button is-static">h</span>
                            </p>

                            <b-input :disabled="iniDisabled" v-model="minutes" class="minutes" />
                            <p class="control">
                                <span class="button is-static">min</span>
                            </p>
                        </b-field>
                    </template>
                    <!--HOURS-->
                    <template v-else-if="type == 'HOURS'">
                        <b-field>
                            <b-input
                                :disabled="iniDisabled"
                                v-model="hoursValue"
                                type="time"
                                :step="step"
                                @input="onChange" />
                        </b-field>
                    </template>
                </template>
            </div>

            <!--append-->
            <div class="control" v-if="!!this.$slots.append">
                <slot name="append" />
            </div>
        </div>

        <span v-if="v && v.$anyError" class="help is-danger">
            <slot name="onError" />
        </span>
        <span class="help info" v-if="!!this.$slots.help">
            <slot name="help" />
        </span>
    </div>
</template>

<script>
const dateFromNow = () => import('./../public/dateFromNow.vue');

import { debounce, formatDate, formatDateTime, DATETIMEFORMAT, DATEFORMAT } from './../mixins/helpers.js';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faCalendarDay, faAngleRight, faAngleLeft } from '@fortawesome/free-solid-svg-icons';
library.add(faCalendarDay);
library.add(faAngleRight);
library.add(faAngleLeft);

export default {
    name: 'dateInput',
    components: { dateFromNow },
    props: {
        type: { type: String, default: 'DATE' },
        v: [Object, Function],
        label: String,
        iniDisabled: { type: Boolean, default: false },
        value: [String, Object, Boolean, Array, Date, Number],
        placeholder: String,
        classes: String,
        isMultiple: Boolean, //further todo
        appendToBody: { type: Boolean, default: true },
        dateFromNow: { type: Boolean, default: false },
        position: {
            type: String,
            default: 'is-bottom-right',
        },
        disablePast: { type: Boolean, default: false },
        isEditable: { type: Boolean, default: true },
        step: { type: Number, default: 300 },
        debounced: { type: Boolean, default: false },
        debounceInterval: { type: Number, default: 700 },
        focusedDate: { type: Date },
    },
    data() {
        return {
            selected: null,
            hours: null,
            minutes: null,
            hoursValue: null,
            formatDate: formatDate,
            timepickerOptions: {
                incrementMinutes: 5,
            },
            datepickerOptions: {
                firstDayOfWeek: 1,
            },
        };
    },
    computed: {
        _debounceInterval() {
            if (this.debounced) {
                return this.debounceInterval;
            }
            return 0;
        },
        selectedAsDate: {
            get: function () {
                if (this.selected) {
                    return new Date(this.selected);
                }
                return null;
            },
            set: function (newValue) {
                if (newValue) {
                    this.selected = newValue.toISOString();
                } else {
                    this.selected = null;
                }
            },
        },
        time() {
            return Number(this.hours) + Number(this.minutes) / 60;
        },
        readOnlyValue() {
            var result = '';
            if (this.isMultiple) {
                result = this.selected.join(', ');
            } else if (this.type == 'DATETIME') {
                result = formatDateTime(this.selected);
            } else if (this.type == 'TIME') {
                if (this.hours) {
                    result = this.hours + 'h ';
                }
                if (this.minutes) {
                    result += this.minutes + 'min';
                }
            } else if (this.type == 'HOURS') {
                result = this.selected;
            } else {
                result = formatDate(this.selected);
            }
            return result;
        },
        minDate() {
            if (this.disablePast) {
                let today = new Date();
                today.setDate(today.getDate() - 1);
                return today;
            } else {
                return null;
            }
        },
        dateFormat() {
            return this.type == 'DATETIME' ? DATETIMEFORMAT : DATEFORMAT;
        },
        name() {
            //get name from label
            if (this.label) {
                return this.label.trim().toLowerCase().replace(' ', '');
            } else {
                return false;
            }
        },
        isRequired() {
            return this.v && this.v.required === false;
        },
    },
    watch: {
        value() {
            this.handleValue();
        },
        time: debounce(function (newVal) {
            this.onChange(newVal);
        }, 500),
    },
    created() {
        this.handleValue();
    },
    methods: {
        formatDateTime,
        handleValue() {
            //if isMultiple, selected must be an array
            if (this.isMultiple) {
                this.selected = Array.isArray(this.value) ? this.value : [this.value];
            } else {
                this.selected = this.value;
            }
            if (this.type == 'TIME') {
                //convert time value
                this.assignHoursAndMinutes(this.value);
            }
            if (this.type == 'HOURS') {
                this.hoursValue = this.value;
            }
        },
        assignHoursAndMinutes(num) {
            if (num) {
                var hrs = parseInt(num);
                var min = Math.round((num - hrs) * 60);
                this.hours = hrs;
                this.minutes = min;
            } else {
                this.hours = 0;
                this.minutes = 0;
            }
        },
        onChange(value) {
            //Probably not the best way to deal with the UTC/non-UTC conversion.
            if (this.type == 'DATE' && value) {
                // Adjust to get the UTC equivalent of the local time
                // This preserves the local time that the user selected
                value = new Date(
                    Date.UTC(
                        value.getFullYear(),
                        value.getMonth(),
                        value.getDate(),
                        this.type == 'DATETIME' ? value.getHours() : 0,
                        this.type == 'DATETIME' ? value.getMinutes() : 0,
                        0,
                        0
                    )
                ).toISOString();
            }
            //manual form of debouncing
            clearTimeout(this.timeoutID);
            this.timeoutID = setTimeout(() => {
                this.selected = value;
                this.$emit('input', value);
            }, this._debounceInterval);
        },
    },
    i18n: {
        messages: {
            en: {
                clickToSelect: 'Click to select',
            },
            fr: {
                clickToSelect: 'Cliquez pour sélectionner',
            },
            nl: {
                clickToSelect: 'Klik om te selecteren',
            },
        },
    },
};
</script>
